import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, MapPin, Users, Calendar, Moon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import type { SearchParams } from '../types';
import { usePlacesSummary } from '../hooks/useApi';

function toIso(date: Date | undefined): string {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
}

function fromIso(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatDisplay(iso: string): string {
  if (!iso) return '';
  const d = fromIso(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SearchFormProps {
  onSearch: (params: SearchParams) => void;
  initialValues?: SearchParams;
  compact?: boolean;
}

export function SearchForm({ onSearch, initialValues = {}, compact = false }: SearchFormProps) {
  const [location, setLocation] = useState(initialValues.location || '');
  const [startDate, setStartDate] = useState(initialValues.startDate || '');
  const [nights, setNights] = useState(initialValues.nights || 2);
  const [guests, setGuests] = useState(initialValues.guests || 2);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => new Date(), []);

  const arrivalDate = fromIso(startDate);

  const handleArrivalSelect = (day: Date | undefined) => {
    setStartDate(toIso(day));
    setIsCalendarOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  const { data: summaries = [], isLoading } = usePlacesSummary();

  const locationSuggestions = useMemo(() => {
    const unique = new Map<
      string,
      {
        value: string;
        type: 'Park' | 'Campground';
      }
    >();

    for (const summary of summaries) {
      const key = summary.placeName.trim().toLowerCase();
      if (!key || unique.has(key)) {
        continue;
      }
      unique.set(key, { value: summary.placeName, type: 'Park' });

      for (const facilityName of summary.facilityNames) {
        const fKey = facilityName.trim().toLowerCase();
        if (!fKey || unique.has(fKey)) {
          continue;
        }
        unique.set(fKey, { value: facilityName, type: 'Campground' });
      }
    }

    const all = Array.from(unique.values()).sort((a, b) =>
      a.value.localeCompare(b.value)
    );
    const query = location.trim().toLowerCase();

    if (!query) {
      const FEATURED_PARKS = [
        'Angel Island',
        'Yosemite',
        'Joshua Tree',
        'Big Sur',
        'Big Basin',
        'Mount Tamalpais',
        'Henry Cowell',
        'Samuel P. Taylor',
        'Anza-Borrego',
        'Humboldt Redwoods',
      ];
      const featured: typeof all = [];
      const usedKeys = new Set<string>();
      for (const name of FEATURED_PARKS) {
        const lower = name.toLowerCase();
        const match = all.find(
          (item) =>
            item.value.toLowerCase().includes(lower) &&
            !usedKeys.has(item.value.toLowerCase()),
        );
        if (match) {
          featured.push(match);
          usedKeys.add(match.value.toLowerCase());
        }
      }
      for (const item of all) {
        if (featured.length >= 10) break;
        if (!usedKeys.has(item.value.toLowerCase())) {
          featured.push(item);
        }
      }
      return featured.slice(0, 10);
    }

    const startsWithMatches = all.filter((item) =>
      item.value.toLowerCase().startsWith(query)
    );
    const includesMatches = all.filter(
      (item) =>
        !item.value.toLowerCase().startsWith(query) &&
        item.value.toLowerCase().includes(query)
    );

    return [...startsWithMatches, ...includesMatches].slice(0, 10);
  }, [summaries, location]);

  const showSuggestions =
    isLocationFocused &&
    (locationSuggestions.length > 0 || isLoading);

  const selectLocationSuggestion = (value: string) => {
    setLocation(value);
    setIsLocationFocused(false);
    setActiveSuggestionIndex(-1);
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((current) =>
        Math.min(current + 1, locationSuggestions.length - 1)
      );
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((current) => Math.max(current - 1, -1));
      return;
    }

    if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      selectLocationSuggestion(locationSuggestions[activeSuggestionIndex].value);
      return;
    }

    if (e.key === 'Escape') {
      setIsLocationFocused(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({
      location: location || undefined,
      startDate: startDate || undefined,
      nights: nights >= 1 ? nights : undefined,
      guests: guests || undefined,
    });
  };

  const inputClass = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all";

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Where do you want to camp?"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setActiveSuggestionIndex(-1);
            }}
            onFocus={() => setIsLocationFocused(true)}
            onBlur={() => {
              window.setTimeout(() => {
                setIsLocationFocused(false);
                setActiveSuggestionIndex(-1);
              }, 100);
            }}
            onKeyDown={handleLocationKeyDown}
            className={`${inputClass} pl-10`}
          />
          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg z-20 overflow-hidden">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading suggestions...</div>
              ) : (
                locationSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.type}:${suggestion.value}`}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectLocationSuggestion(suggestion.value);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm border-b border-gray-100 last:border-b-0 ${
                      index === activeSuggestionIndex
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{suggestion.value}</span>
                    <span className="ml-2 text-xs text-gray-500">{suggestion.type}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
        >
          <Search className="w-5 h-5" />
          Search
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Location search bar */}
      <div className="relative">
        <div className="flex items-center bg-white rounded-full shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <MapPin className="w-5 h-5 text-gray-400 shrink-0 ml-5" />
          <input
            type="text"
            placeholder="Where do you want to camp?"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setActiveSuggestionIndex(-1);
            }}
            onFocus={() => setIsLocationFocused(true)}
            onBlur={() => {
              window.setTimeout(() => {
                setIsLocationFocused(false);
                setActiveSuggestionIndex(-1);
              }, 100);
            }}
            onKeyDown={handleLocationKeyDown}
            className="flex-1 min-w-0 px-3 py-3.5 bg-transparent outline-none text-gray-900 placeholder-gray-400"
          />
          <button
            type="submit"
            className="m-1.5 p-3 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors shrink-0"
            title="Search Campgrounds"
          >
            <Search className="w-5 h-5" />
          </button>
        </div>
        {showSuggestions && (
          <div className="absolute top-full left-0 right-0 mt-2 rounded-lg border border-gray-200 bg-white shadow-lg z-20 overflow-hidden">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Loading suggestions...</div>
            ) : (
              locationSuggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}:${suggestion.value}`}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectLocationSuggestion(suggestion.value);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm border-b border-gray-100 last:border-b-0 ${
                    index === activeSuggestionIndex
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium">{suggestion.value}</span>
                  <span className="ml-2 text-xs text-gray-500">{suggestion.type}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Date / Nights / Guests row */}
      <div className="flex items-center justify-center gap-2">
        <div className="relative" ref={calendarRef}>
          <button
            type="button"
            onClick={() => setIsCalendarOpen((o) => !o)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
          >
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>
              {startDate ? formatDisplay(startDate) : 'Arrival date'}
            </span>
          </button>

          {isCalendarOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-4 w-max max-w-[calc(100vw-2rem)]">
              <DayPicker
                mode="single"
                selected={arrivalDate}
                onSelect={handleArrivalSelect}
                numberOfMonths={2}
                classNames={{ months: 'flex flex-col sm:flex-row gap-4' }}
                disabled={{ before: today }}
                defaultMonth={arrivalDate ?? today}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white">
          <Moon className="w-3.5 h-3.5 text-gray-400" />
          <input
            type="number"
            min={1}
            max={14}
            value={nights}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10);
              setNights(Number.isFinite(parsed) && parsed >= 1 ? parsed : 1);
            }}
            className="w-8 bg-transparent outline-none text-sm text-gray-900 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            title="Nights"
          />
          <span className="text-sm text-gray-500">nights</span>
        </div>

        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white">
          <Users className="w-3.5 h-3.5 text-gray-400" />
          <input
            type="number"
            min={1}
            max={20}
            value={guests}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10);
              setGuests(Number.isFinite(parsed) ? parsed : 1);
            }}
            className="w-8 bg-transparent outline-none text-sm text-gray-900 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            title="Guests"
          />
          <span className="text-sm text-gray-500">guests</span>
        </div>
      </div>
    </form>
  );
}
